// DashboardTreeSidebar — the Dashboards page's binding of the shared
// ExplorerSidebar. It supplies the dashboard leaf icon and the
// dashboard/folder action vocabulary; all tree rendering, selection states,
// the count badge, and chrome live in ExplorerSidebar. The parent's props are
// unchanged — selection is mapped onto the shared `ExplorerSelection` here.

import {
  Camera,
  ClipboardPaste,
  Copy,
  FolderInput,
  FolderPlus,
  LayoutDashboard,
  Pencil,
  Plus,
  Scissors,
  Trash2,
  Undo2
} from 'lucide-react'
import type { ReactNode } from 'react'
import type { Dashboard, DashboardFolder } from '@axilon-apps/domains-ontology-ui-state'

import {
  explorerModShortcut,
  ExplorerSidebar,
  type ExplorerClipboardEntry,
  type ExplorerMenuEntry,
  type ExplorerSelection,
  type ExplorerTreeNode
} from '../../components/explorer'
import { DASHBOARDS_EXPLORER_OPEN_KEY, isAgentInsightsFolderPath } from './explorer-paths'

export type DashboardSelection =
  | { kind: 'folder'; path: string }
  | { kind: 'dashboard'; id: string }
  | { kind: 'none' }

/**
 * Discriminated union of every action the sidebar can request from the parent
 * page. Centralizing these keeps prop drilling tractable and lets the page
 * route them through a single switch.
 *
 * `parentPath` is the path the new item should live under (so context-menu
 * "New … here" pre-fills that folder; header New folder/dashboard uses the
 * current selection; root context menu uses "").
 */
export type DashboardSidebarAction =
  | { kind: 'create-folder'; parentPath: string }
  | { kind: 'create-dashboard'; parentPath: string }
  | { kind: 'autogen-gwbk' }
  | { kind: 'rename-folder'; path: string; name: string; folder?: DashboardFolder }
  | { kind: 'rename-dashboard'; dashboard: Dashboard }
  | { kind: 'move-folder'; path: string; name: string }
  | { kind: 'move-dashboard'; dashboard: Dashboard }
  | { kind: 'drop-folder'; fromPath: string; toParentPath: string }
  | { kind: 'drop-dashboard'; dashboard: Dashboard; toParentPath: string }
  | { kind: 'copy-folder'; path: string; name: string }
  | { kind: 'cut-folder'; path: string; name: string }
  | { kind: 'copy-dashboard'; dashboard: Dashboard }
  | { kind: 'cut-dashboard'; dashboard: Dashboard }
  | { kind: 'paste'; parentPath: string }
  | { kind: 'undo' }
  | { kind: 'delete-folder'; path: string; name: string; folder?: DashboardFolder }
  | { kind: 'delete-dashboard'; dashboard: Dashboard }

export interface DashboardTreeSidebarProps {
  folders: DashboardFolder[]
  dashboards: Dashboard[]
  selection: DashboardSelection
  onSelectFolder: (path: string) => void
  onSelectDashboard: (id: string) => void
  expanded: Set<string>
  onToggle: (path: string) => void
  onAction: (action: DashboardSidebarAction) => void
  filter: string
  onFilterChange: (filter: string) => void
  isLoading?: boolean
  /**
   * Optional content rendered above the sidebar header — used by the
   * Dashboards/Templates tab strip so the toggle sits at the very top of the
   * explorer column.
   */
  topSlot?: ReactNode
  /**
   * Parent path for header New folder / New dashboard. The current folder
   * when a folder is selected, otherwise the parent of the open dashboard.
   * Root context-menu create still uses `""`.
   */
  createParentPath?: string
  clipboard?: ExplorerClipboardEntry | null
  undoLabel?: string | null
  /** Show "Autogenerate from gwbk", which runs on ingest jobs. */
  canAutogenerate?: boolean
}

const toExplorerSelection = (selection: DashboardSelection): ExplorerSelection =>
  selection.kind === 'dashboard' ? { kind: 'item', id: selection.id } : selection

const DashboardTreeSidebar = ({
  folders,
  dashboards,
  selection,
  onSelectFolder,
  onSelectDashboard,
  expanded,
  onToggle,
  onAction,
  filter,
  onFilterChange,
  isLoading,
  topSlot,
  createParentPath = '',
  clipboard = null,
  undoLabel = null,
  canAutogenerate = false
}: DashboardTreeSidebarProps) => {
  const canPaste = clipboard !== null
  const copyShortcut = explorerModShortcut('C')
  const cutShortcut = explorerModShortcut('X')
  const pasteShortcut = explorerModShortcut('V')
  const undoShortcut = explorerModShortcut('Z')
  const dimmedFolderPaths =
    clipboard?.mode === 'cut' && clipboard.kind === 'folder' ? new Set([clipboard.path]) : undefined
  const dimmedItemIds =
    clipboard?.mode === 'cut' && clipboard.kind === 'item' ? new Set([clipboard.id]) : undefined

  const folderMenu = (node: ExplorerTreeNode<DashboardFolder, Dashboard>): ExplorerMenuEntry[] => {
    const folder = node.folder
    if (isAgentInsightsFolderPath(node.path)) return []

    const displayName = folder?.name ?? node.name
    const entries: ExplorerMenuEntry[] = [
      {
        icon: FolderPlus,
        label: 'New folder here',
        onSelect: () => onAction({ kind: 'create-folder', parentPath: node.path })
      },
      {
        icon: Plus,
        label: 'New dashboard here',
        onSelect: () => onAction({ kind: 'create-dashboard', parentPath: node.path })
      },
      {
        icon: Pencil,
        label: 'Rename',
        separatorBefore: true,
        onSelect: () =>
          onAction({ kind: 'rename-folder', path: node.path, name: displayName, folder })
      },
      {
        icon: FolderInput,
        label: 'Move to…',
        onSelect: () => onAction({ kind: 'move-folder', path: node.path, name: displayName })
      },
      {
        icon: Copy,
        label: 'Copy',
        shortcut: copyShortcut,
        onSelect: () => onAction({ kind: 'copy-folder', path: node.path, name: displayName })
      },
      {
        icon: Scissors,
        label: 'Cut',
        shortcut: cutShortcut,
        onSelect: () => onAction({ kind: 'cut-folder', path: node.path, name: displayName })
      },
      {
        icon: ClipboardPaste,
        label: 'Paste',
        shortcut: pasteShortcut,
        disabled: !canPaste,
        onSelect: () => onAction({ kind: 'paste', parentPath: node.path })
      },
      {
        icon: Trash2,
        label: 'Delete',
        variant: 'destructive',
        separatorBefore: true,
        onSelect: () =>
          onAction({ kind: 'delete-folder', path: node.path, name: displayName, folder })
      }
    ]
    return entries
  }

  return (
    <ExplorerSidebar<DashboardFolder, Dashboard>
      heading="Dashboards"
      ariaLabel="Dashboard folders"
      folders={folders}
      items={dashboards}
      itemIcon={LayoutDashboard}
      testIdPrefix="dashboard-tree"
      selection={toExplorerSelection(selection)}
      expanded={expanded}
      filter={filter}
      isLoading={isLoading}
      topSlot={topSlot}
      collapseStorageKey={DASHBOARDS_EXPLORER_OPEN_KEY}
      emptyHint="No folders yet. Create one to get started."
      headerButtons={[
        ...(canAutogenerate
          ? [
              {
                icon: Camera,
                label: 'Autogenerate from gwbk',
                onClick: () => onAction({ kind: 'autogen-gwbk' })
              }
            ]
          : []),
        {
          icon: FolderPlus,
          label: 'New folder',
          onClick: () => onAction({ kind: 'create-folder', parentPath: createParentPath })
        },
        {
          icon: Plus,
          label: 'New dashboard',
          onClick: () => onAction({ kind: 'create-dashboard', parentPath: createParentPath })
        }
      ]}
      folderMenu={folderMenu}
      itemMenu={(dashboard) => [
        {
          icon: Pencil,
          label: 'Rename',
          onSelect: () => onAction({ kind: 'rename-dashboard', dashboard })
        },
        {
          icon: FolderInput,
          label: 'Move to…',
          onSelect: () => onAction({ kind: 'move-dashboard', dashboard })
        },
        {
          icon: Copy,
          label: 'Copy',
          shortcut: copyShortcut,
          onSelect: () => onAction({ kind: 'copy-dashboard', dashboard })
        },
        {
          icon: Scissors,
          label: 'Cut',
          shortcut: cutShortcut,
          onSelect: () => onAction({ kind: 'cut-dashboard', dashboard })
        },
        {
          icon: Trash2,
          label: 'Delete',
          variant: 'destructive',
          separatorBefore: true,
          onSelect: () => onAction({ kind: 'delete-dashboard', dashboard })
        }
      ]}
      rootMenu={[
        {
          icon: FolderPlus,
          label: 'New folder',
          onSelect: () => onAction({ kind: 'create-folder', parentPath: '' })
        },
        {
          icon: Plus,
          label: 'New dashboard',
          onSelect: () => onAction({ kind: 'create-dashboard', parentPath: '' })
        },
        {
          icon: ClipboardPaste,
          label: 'Paste',
          shortcut: pasteShortcut,
          disabled: !canPaste,
          onSelect: () => onAction({ kind: 'paste', parentPath: '' })
        },
        {
          icon: Undo2,
          label: undoLabel ?? 'Undo',
          shortcut: undoShortcut,
          disabled: undoLabel === null,
          onSelect: () => onAction({ kind: 'undo' })
        }
      ]}
      onToggle={onToggle}
      onSelectFolder={onSelectFolder}
      onSelectItem={onSelectDashboard}
      onFilterChange={onFilterChange}
      onMoveItem={(dashboard, toParentPath) =>
        onAction({ kind: 'drop-dashboard', dashboard, toParentPath })
      }
      onMoveFolder={(fromPath, toParentPath) =>
        onAction({ kind: 'drop-folder', fromPath, toParentPath })
      }
      isLockedFolder={isAgentInsightsFolderPath}
      dimmedFolderPaths={dimmedFolderPaths}
      dimmedItemIds={dimmedItemIds}
    />
  )
}

export default DashboardTreeSidebar
